import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarCheck, CheckCircle2, Home, Lock, Sparkles } from "lucide-react";
import SEOHead from "../components/SEOHead";
import { api } from "../lib/api";

const DIAGNOSTIC_CONFIRM_TOKEN =
  process.env.REACT_APP_DIAGNOSTIC_CONFIRM_TOKEN || "sfdiag-zcal-confirm-7f3k9p2x-2026";

export default function DiagnosticConfirme() {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");

    const params = new URLSearchParams(window.location.search);
    const source = params.get("source");
    const token = params.get("token");

    const validRedirect = source === "zcal" && token === DIAGNOSTIC_CONFIRM_TOKEN;

    if (!validRedirect) {
      setIsAuthorized(false);
      navigate("/", { replace: true });
      return;
    }

    setIsAuthorized(true);

    // Le token sert uniquement de clé d'entrée depuis Zcal.
    // Dès que la page est validée, on nettoie l'URL visible pour éviter d'exposer le token.
    window.history.replaceState({}, "", "/diagnostic-confirme");

    api.post("/tracking/event", {
      event: "diagnostic_booking_confirmed",
      path: "/diagnostic-confirme",
      metadata: {
        source: "zcal_redirect",
        token,
        protected_redirect: "true",
      },
    }).catch(() => {});
  }, [navigate]);

  if (!isAuthorized) {
    return (
      <div className="bg-brand-mint min-h-screen flex items-center justify-center px-4" data-testid="diagnostic-confirme-locked">
        <SEOHead
          title="Page non accessible | SF Learning"
          description="Cette page de confirmation est réservée aux personnes ayant finalisé une réservation."
          url={typeof window !== "undefined" ? window.location.href : ""}
          type="website"
        />
        <div className="card bg-white/90 max-w-lg w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 text-brand-orange flex items-center justify-center mx-auto mb-5">
            <Lock className="w-8 h-8" />
          </div>
          <h1 className="font-heading text-2xl font-black">Page non accessible directement</h1>
          <p className="text-gray-600 mt-3">
            Cette page apparaît uniquement après une réservation validée depuis le calendrier SF Learning.
          </p>
          <Link to="/" className="btn-primary mt-6 inline-flex">
            <Home className="w-4 h-4" /> Retour à l’accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-mint min-h-screen" data-testid="diagnostic-confirme-page">
      <SEOHead
        title="Réservation confirmée | SF Learning"
        description="Ton diagnostic stratégique offert est bien réservé. Prépare ton échange avec SF Learning."
        url={typeof window !== "undefined" ? window.location.origin + "/diagnostic-confirme" : ""}
        type="website"
      />

      <section className="relative overflow-hidden border-b border-emerald-100">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div
          className="absolute right-[-180px] top-[-220px] w-[560px] h-[560px] rounded-full"
          style={{ background: "radial-gradient(closest-side, rgba(244,161,0,0.22), transparent 70%)" }}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative text-center">
          <span className="badge mb-6 mx-auto w-fit">
            <Sparkles className="w-3.5 h-3.5" /> Diagnostic stratégique SF Learning
          </span>

          <div className="w-20 h-20 rounded-full bg-emerald-100 text-brand-green flex items-center justify-center mx-auto mb-6 shadow-sm">
            <CheckCircle2 className="w-11 h-11" />
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
            Merci, ton rendez-vous est <span className="text-brand-green">bien réservé</span>
          </h1>

          <p className="mt-6 text-lg text-gray-700 max-w-2xl mx-auto leading-relaxed">
            Tu vas recevoir les informations de confirmation directement via Zcal.
            De mon côté, je serai notifié automatiquement pour préparer ton diagnostic stratégique.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
            <div className="card p-5 bg-white/90">
              <CalendarCheck className="w-6 h-6 text-brand-green mb-3" />
              <p className="font-bold">Vérifie ton email</p>
              <p className="text-sm text-gray-600 mt-1">Tu recevras l’invitation et les détails du créneau choisi.</p>
            </div>
            <div className="card p-5 bg-white/90">
              <CheckCircle2 className="w-6 h-6 text-brand-green mb-3" />
              <p className="font-bold">Prépare ton contexte</p>
              <p className="text-sm text-gray-600 mt-1">Note tes blocages actuels de recrutement, d’équipe ou de croissance.</p>
            </div>
            <div className="card p-5 bg-white/90">
              <ArrowRight className="w-6 h-6 text-brand-green mb-3" />
              <p className="font-bold">On passe à l’action</p>
              <p className="text-sm text-gray-600 mt-1">L’objectif : repartir avec une lecture claire et une prochaine étape concrète.</p>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/" className="btn-primary">
              <Home className="w-4 h-4" /> Retour à l’accueil
            </Link>
            <Link to="/catalog" className="btn-ghost">
              Voir les accompagnements <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
